import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  Grid,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Chip,
  Avatar,
  Rating,
  Snackbar,
  Alert,
  Dialog,
  IconButton,
  AppBar,
  Toolbar,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';

// Icons
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PhoneIcon from '@mui/icons-material/Phone';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import StarIcon from '@mui/icons-material/Star';
import CloseIcon from '@mui/icons-material/Close';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import VerifiedIcon from '@mui/icons-material/Verified';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import GroupsIcon from '@mui/icons-material/Groups';
import BuildIcon from '@mui/icons-material/Build';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';

const PHONE_NUMBER = '0536055502';
const WHATSAPP_NUMBER = '972536055502';

// צבעים - אדום, שחור, לבן
const colors = {
  primary: '#DC2626', // אדום חזק
  secondary: '#B91C1C', // אדום כהה
  accent: '#EF4444', // אדום בהיר
  dark: '#0A0A0A', // שחור
  darkGray: '#1F1F1F', // אפור כהה
  light: '#FAFAFA', // לבן-אפור
  white: '#FFFFFF',
  gray: '#6B7280',
  lightGray: '#E5E7EB',
  gold: '#FBBF24',
};

const HagraristLandingPage = () => {
  const [formData, setFormData] = useState({ name: '', phone: '', location: '' });
  const [showSuccess, setShowSuccess] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [liveViewers, setLiveViewers] = useState(7);

  // Popup timer
  useEffect(() => {
    const timer = setTimeout(() => {
      const seen = sessionStorage.getItem('seenHagraristPopup');
      if (!seen) setShowPopup(true);
    }, 4000);
    return () => clearTimeout(timer);
  }, []);

  // Navbar scroll
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Live viewers
  useEffect(() => {
    const interval = setInterval(() => {
      setLiveViewers(prev => Math.max(4, Math.min(15, prev + (Math.random() > 0.5 ? 1 : -1))));
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const closePopup = () => {
    setShowPopup(false);
    sessionStorage.setItem('seenHagraristPopup', 'true');
  };

  const handleWhatsApp = (msg = 'היי! אני צריך גרר לאופנוע 🏍️') => {
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const handleCall = () => {
    window.location.href = `tel:${PHONE_NUMBER}`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const msg = `היי! קוראים לי ${formData.name}.\nאני נמצא ב: ${formData.location}\nטלפון: ${formData.phone}\nאני צריך גרר לאופנוע! 🏍️`;
    handleWhatsApp(msg);
    setShowSuccess(true);
  };

  const scrollToForm = () => {
    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Services
  const services = [
    { icon: <TwoWheelerIcon sx={{ fontSize: 40 }} />, title: 'גרירת אופנועים', desc: 'כל סוגי האופנועים - ספורט, אנדורו, קרוזר' },
    { icon: <LocalShippingIcon sx={{ fontSize: 40 }} />, title: 'גרירת קטנועים', desc: 'קטנועים מכל הגדלים והסוגים' },
    { icon: <BuildIcon sx={{ fontSize: 40 }} />, title: 'גרירת טרקטורונים', desc: 'ATV וכלי רכב שטח' },
    { icon: <NightsStayIcon sx={{ fontSize: 40 }} />, title: 'שירות 24/7', desc: 'זמינים גם בלילות וסופי שבוע', highlight: true },
  ];

  // Why us
  const advantages = [
    { icon: <SpeedIcon />, title: 'הגעה מהירה', desc: 'זמן הגעה מהיר במיוחד לכל מקום במרכז' },
    { icon: <AttachMoneyIcon />, title: 'מחיר הוגן', desc: 'מחירים שקופים ונוחים לכל כיס' },
    { icon: <VerifiedIcon />, title: 'שירות מקצועי', desc: '3 שנות ניסיון בגרירת אופנועים' },
    { icon: <AccessTimeIcon />, title: 'זמינות 24/7', desc: 'תמיד כאן בשבילך - יום ולילה' },
    { icon: <SecurityIcon />, title: 'הובלה בטוחה', desc: 'ציוד מקצועי לאבטחת האופנוע' },
    { icon: <SupportAgentIcon />, title: 'ייעוץ חינם', desc: 'נתקעת? מתקשרים ומייעצים בחינם' },
  ];

  // Testimonials
  const testimonials = [
    { name: 'יוסי מ.', location: 'תל אביב', text: 'הגיע תוך 20 דקות! שירות מעולה ומחיר הוגן. ממליץ בחום.', rating: 5 },
    { name: 'דני כ.', location: 'רמת גן', text: 'נתקעתי ב-2 בלילה והגרריסט הגיע מהר. הציל אותי!', rating: 5 },
    { name: 'אבי ש.', location: 'פתח תקווה', text: 'מקצועי, אמין, ומחיר טוב. השתמשתי פעמיים כבר.', rating: 5 },
    { name: 'רון ל.', location: 'ראשון לציון', text: 'האופנוע הגיע במצב מושלם. טיפול זהיר ואכפתי.', rating: 5 },
  ];

  // FAQ
  const faqItems = [
    { q: 'כמה זמן לוקח לכם להגיע?', a: 'זמן ההגעה תלוי במיקום, אבל בדרך כלל אנחנו מגיעים תוך 20-40 דקות באזור המרכז. בשעות העומס זה יכול לקחת קצת יותר.' },
    { q: 'כמה עולה גרירה?', a: 'המחיר תלוי במרחק ובסוג הרכב. אנחנו נותנים הצעת מחיר שקופה לפני שמתחילים. התקשרו ונגיד לכם בדיוק כמה זה יעלה.' },
    { q: 'אתם עובדים בלילה?', a: 'כן! אנחנו זמינים 24 שעות ביממה, 7 ימים בשבוע. גם ב-3 בלילה - תתקשרו ונגיע.' },
    { q: 'האם אתם גוררים גם קטנועים קטנים?', a: 'בהחלט! אנחנו גוררים הכל - מקטנוע 50 סמ"ק ועד אופנוע כבד. גם טרקטורונים ו-ATV.' },
    { q: 'לאיזה אזורים אתם מגיעים?', a: 'אנחנו מכסים את כל אזור המרכז וגוש דן. פתח תקווה, תל אביב, רמת גן, ראשון לציון, חולון ועוד.' },
    { q: 'מה אם האופנוע תקוע במקום לא נגיש?', a: 'אין בעיה! יש לנו ציוד מתאים לחילוץ ממקומות קשים. ספרו לנו על המיקום ונגיע מוכנים.' },
  ];

  return (
    <Box sx={{ 
      background: colors.light, 
      minHeight: '100vh', 
      direction: 'rtl',
      width: '100%',
      overflowX: 'hidden',
    }}>
      
      {/* Navbar */}
      <AppBar 
        position="fixed" 
        elevation={scrolled ? 4 : 0}
        sx={{ 
          background: scrolled ? 'rgba(10, 10, 10, 0.95)' : 'transparent',
          backdropFilter: scrolled ? 'blur(10px)' : 'none',
          transition: 'all 0.3s ease',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', py: 1, px: { xs: 1.5, md: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TwoWheelerIcon sx={{ color: colors.primary, fontSize: { xs: 28, md: 32 } }} />
            <Typography sx={{ fontWeight: 800, color: '#fff', fontSize: { xs: '0.9rem', md: '1.2rem' } }}>
              הגרריסט
            </Typography>
          </Box>
          
          <Stack direction="row" spacing={{ xs: 0.5, md: 2 }} alignItems="center">
            <Chip
              icon={<GroupsIcon sx={{ color: '#4ADE80 !important', fontSize: 16 }} />}
              label={`${liveViewers} צופים`}
              size="small"
              sx={{ 
                background: 'rgba(74, 222, 128, 0.15)',
                color: '#4ADE80',
                fontWeight: 600,
                display: { xs: 'none', md: 'flex' },
              }}
            />
            
            <Button
              variant="contained"
              size="small"
              startIcon={<PhoneIcon />}
              onClick={handleCall}
              sx={{
                background: colors.primary,
                fontWeight: 700,
                borderRadius: 2,
                gap: 1,
                '&:hover': { background: colors.secondary },
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{PHONE_NUMBER}</Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>התקשר</Box>
            </Button>
            
            <Button
              variant="contained"
              size="small"
              onClick={() => handleWhatsApp()}
              sx={{
                background: '#25D366',
                minWidth: 'auto',
                p: 1,
                borderRadius: 2,
                '&:hover': { background: '#20BD5A' },
              }}
            >
              <WhatsAppIcon />
            </Button>
          </Stack>
        </Toolbar>
      </AppBar>

      {/* Emergency Popup */}
      <Dialog
        open={showPopup}
        onClose={closePopup}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 4, overflow: 'hidden', m: 2 }
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <IconButton
            onClick={closePopup}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 10,
              background: 'rgba(255,255,255,0.9)',
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box sx={{ 
            background: `linear-gradient(135deg, ${colors.dark} 0%, ${colors.primary} 100%)`,
            p: 3,
            textAlign: 'center',
          }}>
            <motion.div animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 1.5, repeat: Infinity }}>
              <TwoWheelerIcon sx={{ fontSize: 60, color: colors.gold }} />
            </motion.div>
            <Typography variant="h4" sx={{ color: '#fff', fontWeight: 800, mt: 1 }}>
              🚨 נתקעת עם האופנוע?
            </Typography>
          </Box>

          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: colors.dark, mb: 2 }}>
              ייעוץ טלפוני חינם!
            </Typography>
            
            <Typography sx={{ color: colors.gray, mb: 3 }}>
              לפני שאתה מזמין גרר - תתקשר ונראה אם אפשר לעזור לך בטלפון.
              <br />
              <strong>בלי עלות, בלי התחייבות.</strong>
            </Typography>

            <Stack spacing={2}>
              <Button
                variant="contained"
                size="large"
                fullWidth
                startIcon={<PhoneIcon />}
                onClick={() => { handleCall(); closePopup(); }}
                sx={{
                  background: colors.primary,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  borderRadius: 3,
                  gap: 1.5,
                  '&:hover': { background: colors.secondary },
                }}
              >
                התקשר עכשיו - {PHONE_NUMBER}
              </Button>
              
              <Button
                variant="contained"
                size="large"
                fullWidth
                startIcon={<WhatsAppIcon />}
                onClick={() => { handleWhatsApp('היי! נתקעתי עם האופנוע ואשמח לייעוץ 🏍️'); closePopup(); }}
                sx={{
                  background: '#25D366',
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  borderRadius: 3,
                  gap: 1.5,
                  '&:hover': { background: '#20BD5A' },
                }}
              >
                שלח וואטסאפ
              </Button>
            </Stack>
          </Box>
        </Box>
      </Dialog>

      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${colors.dark} 0%, #1a1a1a 50%, ${colors.darkGray} 100%)`,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          pt: { xs: 12, md: 8 },
          pb: { xs: 4, md: 0 },
        }}
      >
        {/* Background Image */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'url(/images/sport-bike.jpeg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.5,
          }}
        />
        
        {/* Dark Overlay */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to bottom, rgba(10,10,10,0.5) 0%, rgba(10,10,10,0.7) 100%)',
          }}
        />

        {/* Red accent lines */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: `linear-gradient(90deg, transparent, ${colors.primary}, transparent)`,
          zIndex: 2,
        }} />
        
        {/* Background pattern */}
        <Box sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `radial-gradient(circle at 80% 20%, ${colors.primary}22 0%, transparent 40%),
                           radial-gradient(circle at 20% 80%, ${colors.primary}15 0%, transparent 30%)`,
          zIndex: 1,
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 3, px: { xs: 1.5, sm: 3 } }}>
          <Grid container spacing={{ xs: 3, md: 4 }} alignItems="center">
            <Grid item xs={12} md={7}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                {/* Emergency badge */}
                <Box sx={{ textAlign: { xs: 'center', md: 'right' }, mb: 2 }}>
                  <Chip
                    icon={<ElectricBoltIcon sx={{ color: '#fff !important', fontSize: { xs: 16, md: 20 } }} />}
                    label="שירות גרר 24/7 - זמינים עכשיו!"
                    sx={{
                      background: colors.primary,
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: { xs: '0.75rem', md: '1rem' },
                      py: { xs: 2, md: 2.5 },
                      gap: 0.5,
                      '& .MuiChip-label': { px: { xs: 1, md: 2 } },
                    }}
                  />
                </Box>

                <Typography
                  variant="h1"
                  sx={{
                    color: '#fff',
                    fontWeight: 900,
                    fontSize: { xs: '1.6rem', sm: '2rem', md: '3.2rem' },
                    lineHeight: 1.2,
                    mb: { xs: 1.5, md: 2 },
                    textAlign: { xs: 'center', md: 'right' },
                    wordBreak: 'keep-all',
                  }}
                >
                  גרר אופנועים הגעה מהירה,
                  <br />
                  <Box component="span" sx={{ color: colors.primary }}>
                    וחילוץ בלי כאב ראש.
                  </Box>
                </Typography>

                <Typography
                  sx={{
                    color: 'rgba(255,255,255,0.8)',
                    fontSize: { xs: '0.9rem', md: '1.2rem' },
                    mb: { xs: 2, md: 3 },
                    textAlign: { xs: 'center', md: 'right' },
                    maxWidth: { xs: '100%', md: 500 },
                    mx: { xs: 'auto', md: 0 },
                    lineHeight: 1.7,
                    px: { xs: 1, md: 0 },
                  }}
                >
                                  <strong>בטיחות מקצועי</strong> כשאתה תקוע
                  <br />
                  <strong>הובלה בטוחה</strong> וזהירה לאופנוע
                </Typography>

                <Stack 
                  direction={{ xs: 'column', sm: 'row' }} 
                  spacing={{ xs: 1.5, md: 2 }}
                  sx={{ justifyContent: { xs: 'center', md: 'flex-start' }, px: { xs: 1, md: 0 } }}
                >
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<PhoneIcon />}
                    onClick={handleCall}
                    sx={{
                      background: colors.primary,
                      color: '#fff',
                      py: { xs: 1.3, md: 1.8 },
                      px: { xs: 2.5, md: 4 },
                      fontSize: { xs: '0.95rem', md: '1.15rem' },
                      fontWeight: 700,
                      borderRadius: 3,
                      gap: 1,
                      width: { xs: '100%', sm: 'auto' },
                      '&:hover': { background: colors.secondary, transform: 'scale(1.02)' },
                    }}
                  >
                    הזמן גרר עכשיו
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<WhatsAppIcon />}
                    onClick={() => handleWhatsApp()}
                    sx={{
                      borderColor: '#25D366',
                      color: '#25D366',
                      py: { xs: 1.3, md: 1.8 },
                      px: { xs: 2.5, md: 4 },
                      fontSize: { xs: '0.95rem', md: '1.15rem' },
                      fontWeight: 700,
                      borderRadius: 3,
                      borderWidth: 2,
                      gap: 1,
                      width: { xs: '100%', sm: 'auto' },
                      '&:hover': { borderColor: '#20BD5A', background: 'rgba(37,211,102,0.1)' },
                    }}
                  >
                    שלח וואטסאפ
                  </Button>
                </Stack>

                {/* Trust indicators */}
                <Stack 
                  direction="row" 
                  spacing={{ xs: 3, md: 4 }} 
                  sx={{ 
                    mt: { xs: 3, md: 4 }, 
                    justifyContent: { xs: 'center', md: 'flex-start' },
                  }}
                >
                  {[
                    { icon: '⚡', text: 'הגעה מהירה' },
                    { icon: '🕐', text: '24/7' },
                    { icon: '💰', text: 'מחיר הוגן' },
                  ].map((item, i) => (
                    <Box key={i} sx={{ textAlign: 'center' }}>
                      <Typography sx={{ fontSize: { xs: '1.1rem', md: '1.3rem' }, mb: 0.3 }}>{item.icon}</Typography>
                      <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: { xs: '0.7rem', md: '0.85rem' } }}>{item.text}</Typography>
                    </Box>
                  ))}
                </Stack>
              </motion.div>
            </Grid>

            {/* Quick Contact Card */}
            <Grid item xs={12} md={5}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                <Card
                  id="contact-form"
                  sx={{
                    background: 'rgba(255,255,255,0.98)',
                    borderRadius: { xs: 3, md: 4 },
                    p: { xs: 2, md: 4 },
                    boxShadow: '0 25px 50px rgba(0,0,0,0.5)',
                    border: `3px solid ${colors.primary}`,
                    mx: { xs: 0, md: 0 },
                    overflow: 'visible',
                  }}
                >
                  {/* Badge inside card */}
                  <Box sx={{ textAlign: 'center', mb: 1 }}>
                    <Chip
                      icon={<ElectricBoltIcon sx={{ color: '#fff !important', fontSize: { xs: 14, md: 18 } }} />}
                      label="ייעוץ טלפוני חינם!"
                      sx={{
                        background: colors.primary,
                        color: '#fff',
                        fontWeight: 700,
                        py: { xs: 1.5, md: 2 },
                        fontSize: { xs: '0.75rem', md: '0.875rem' },
                      }}
                    />
                  </Box>
                  
                  <Typography variant="h5" sx={{ fontWeight: 800, color: colors.dark, mb: 0.5, textAlign: 'center', fontSize: { xs: '1.1rem', md: '1.4rem' } }}>
                    צריך גרר? נגיע מהר!
                  </Typography>
                  <Typography sx={{ color: colors.gray, mb: { xs: 1.5, md: 3 }, textAlign: 'center', fontSize: { xs: '0.8rem', md: '1rem' } }}>
                    השאר פרטים ונתקשר תוך דקות
                  </Typography>

                  <Box component="form" onSubmit={handleSubmit}>
                    <TextField
                      fullWidth
                      label="שם"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      size="small"
                      sx={{ mb: 1.5 }}
                    />
                    <TextField
                      fullWidth
                      label="טלפון"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      size="small"
                      sx={{ mb: 1.5 }}
                    />
                    <TextField
                      fullWidth
                      label="איפה אתה נמצא?"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      size="small"
                      sx={{ mb: { xs: 1.5, md: 3 } }}
                    />
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      size="large"
                      startIcon={<WhatsAppIcon />}
                      sx={{
                        background: colors.primary,
                        py: { xs: 1.2, md: 1.5 },
                        fontSize: { xs: '0.95rem', md: '1.1rem' },
                        fontWeight: 700,
                        borderRadius: 3,
                        gap: 1,
                        '&:hover': { background: colors.secondary },
                      }}
                    >
                      שלח ונחזור אליך
                    </Button>
                  </Box>

                  <Typography sx={{ textAlign: 'center', mt: 2, color: colors.gray, fontSize: '0.9rem' }}>
                    או התקשר ישירות:
                  </Typography>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="large"
                    startIcon={<PhoneIcon />}
                    onClick={handleCall}
                    sx={{ mt: 1, borderRadius: 3, fontWeight: 700, gap: 1.5, borderColor: colors.primary, color: colors.primary }}
                  >
                    {PHONE_NUMBER}
                  </Button>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Trust Bar */}
      <Box 
        sx={{ 
          background: colors.dark,
          py: { xs: 3, md: 4 },
          borderTop: `4px solid ${colors.primary}`,
          borderBottom: `4px solid ${colors.primary}`,
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 1.5, md: 3 } }}>
          <Grid container spacing={2} alignItems="center" justifyContent="center">
            {[
              { icon: '⭐', value: '100+', label: 'לקוחות מרוצים' },
              { icon: '🏆', value: '3', label: 'שנות ניסיון' },
              { icon: '⚡', value: '24/7', label: 'שירות חירום' },
              { icon: '🏍️', value: '500+', label: 'אופנועים גררנו' },
            ].map((item, i) => (
              <Grid item xs={6} sm={3} key={i}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: { xs: '1.5rem', md: '2rem' }, mb: 0.5 }}>{item.icon}</Typography>
                    <Typography sx={{ color: colors.primary, fontWeight: 800, fontSize: { xs: '1.5rem', md: '2rem' } }}>
                      {item.value}
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: { xs: '0.75rem', md: '0.9rem' } }}>
                      {item.label}
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Problem/Pain Section */}
      <Box sx={{ py: { xs: 6, md: 10 }, background: colors.light }}>
        <Container maxWidth="lg" sx={{ px: { xs: 1.5, md: 3 } }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <Box sx={{ 
                  background: colors.dark, 
                  borderRadius: 4, 
                  p: 4,
                  border: `2px solid ${colors.primary}`,
                }}>
                  <ErrorOutlineIcon sx={{ fontSize: 50, color: colors.primary, mb: 2 }} />
                  <Typography variant="h4" sx={{ color: '#fff', fontWeight: 800, mb: 2 }}>
                    נתקעת עם האופנוע?
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.8)', lineHeight: 1.8 }}>
                    אנחנו מבינים את ההרגשה. האופנוע לא מניע, אתה תקוע באמצע הדרך, לא יודע מה לעשות...
                    <br /><br />
                    <strong style={{ color: colors.primary }}>זה בדיוק בשביל זה אנחנו כאן.</strong>
                    <br /><br />
                    שיחת טלפון אחת - ואנחנו בדרך אליך.
                  </Typography>
                </Box>
              </motion.div>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <Box sx={{ 
                  background: colors.primary, 
                  borderRadius: 4, 
                  p: 4,
                }}>
                  <ThumbUpIcon sx={{ fontSize: 50, color: '#fff', mb: 2 }} />
                  <Typography variant="h4" sx={{ color: '#fff', fontWeight: 800, mb: 2 }}>
                    הפתרון שלנו
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.95)', lineHeight: 1.8 }}>
                    ✓ הגעה מהירה לכל מקום במרכז
                    <br />
                    ✓ ציוד מקצועי להובלה בטוחה
                    <br />
                    ✓ מחיר שקוף - בלי הפתעות
                    <br />
                    ✓ זמינות 24/7 - גם בלילה
                    <br />
                    ✓ יחס אישי וסבלני
                  </Typography>
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Gallery Section - תמונות אמיתיות */}
      <Box sx={{ py: { xs: 4, md: 8 }, background: colors.dark }}>
        <Container maxWidth="lg" sx={{ px: { xs: 1.5, md: 3 } }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 5 } }}>
            <Chip label="עבודות אמיתיות" sx={{ mb: 2, background: 'rgba(255,255,255,0.1)', color: '#fff', fontWeight: 600, fontSize: { xs: '0.75rem', md: '0.875rem' } }} />
            <Typography variant="h2" sx={{ fontWeight: 800, color: '#fff', fontSize: { xs: '1.4rem', sm: '1.8rem', md: '2.5rem' } }}>
              הגרריסט בפעולה
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 1, md: 2 }}>
            {[
              { img: '/images/sport-bike.jpeg', title: 'אופנוע ספורט' },
              { img: '/images/atv.jpeg', title: 'טרקטורון ATV' },
              { img: '/images/enduro.jpeg', title: 'אופנוע אנדורו' },
              { img: '/images/rescue.jpeg', title: 'חילוץ אופנוע' },
            ].map((item, i) => (
              <Grid item xs={6} md={3} key={i}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      borderRadius: { xs: 2, md: 3 },
                      overflow: 'hidden',
                      height: { xs: 140, sm: 180, md: 220 },
                      '&:hover img': { transform: 'scale(1.1)' },
                    }}
                  >
                    <Box
                      component="img"
                      src={item.img}
                      alt={item.title}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.5s ease',
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                        p: { xs: 1, md: 2 },
                      }}
                    >
                      <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: { xs: '0.75rem', md: '1rem' } }}>
                        {item.title}
                      </Typography>
                    </Box>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Services Section */}
      <Box sx={{ py: { xs: 5, md: 10 }, background: colors.white }}>
        <Container maxWidth="lg" sx={{ px: { xs: 1.5, md: 3 } }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 6 } }}>
            <Chip label="השירותים שלנו" sx={{ mb: 2, background: colors.light, color: colors.primary, fontWeight: 600, fontSize: { xs: '0.75rem', md: '0.875rem' } }} />
            <Typography variant="h2" sx={{ fontWeight: 800, color: colors.dark, fontSize: { xs: '1.4rem', sm: '1.6rem', md: '2.5rem' } }}>
              מה אנחנו גוררים?
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 1.5, md: 3 }}>
            {services.map((service, i) => (
              <Grid item xs={6} md={3} key={i}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  whileHover={{ y: -5 }}
                >
                  <Card
                    sx={{
                      p: { xs: 1.5, md: 3 },
                      textAlign: 'center',
                      height: '100%',
                      border: service.highlight ? `3px solid ${colors.primary}` : '1px solid #E5E7EB',
                      borderRadius: { xs: 2, md: 3 },
                      '&:hover': { boxShadow: '0 10px 30px rgba(0,0,0,0.1)' },
                    }}
                  >
                    {service.highlight && (
                      <Chip
                        label="24/7"
                        size="small"
                        sx={{
                          mb: 1,
                          background: colors.primary,
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: { xs: '0.65rem', md: '0.75rem' },
                        }}
                      />
                    )}
                    <Box sx={{ color: colors.primary, mb: { xs: 1, md: 2 }, '& svg': { fontSize: { xs: 28, md: 40 } } }}>{service.icon}</Box>
                    <Typography sx={{ fontWeight: 700, color: colors.dark, mb: { xs: 0.3, md: 1 }, fontSize: { xs: '0.8rem', md: '1rem' } }}>{service.title}</Typography>
                    <Typography sx={{ color: colors.gray, fontSize: { xs: '0.7rem', md: '0.9rem' }, display: { xs: 'none', sm: 'block' } }}>{service.desc}</Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: 'center', mt: { xs: 3, md: 5 } }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<PhoneIcon />}
              onClick={handleCall}
              sx={{
                background: colors.primary,
                py: { xs: 1.2, md: 1.5 },
                px: { xs: 3, md: 4 },
                borderRadius: 3,
                fontWeight: 700,
                fontSize: { xs: '0.95rem', md: '1.1rem' },
                gap: 1,
                '&:hover': { background: colors.secondary },
              }}
            >
              הזמן גרר עכשיו
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Why Us Section */}
      <Box sx={{ py: { xs: 5, md: 10 }, background: colors.light }}>
        <Container maxWidth="lg" sx={{ px: { xs: 1.5, md: 3 } }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 6 } }}>
            <Chip label="למה דווקא אנחנו?" sx={{ mb: 2, background: colors.white, color: colors.primary, fontWeight: 600, fontSize: { xs: '0.75rem', md: '0.875rem' } }} />
            <Typography variant="h2" sx={{ fontWeight: 800, color: colors.dark, fontSize: { xs: '1.4rem', sm: '1.6rem', md: '2.5rem' } }}>
              6 סיבות לבחור בהגרריסט
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 1.5, md: 3 }}>
            {advantages.map((adv, i) => (
              <Grid item xs={6} md={4} key={i}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <Card sx={{ p: { xs: 1.5, md: 3 }, borderRadius: { xs: 2, md: 3 }, height: '100%', textAlign: 'center' }}>
                    <Box sx={{ 
                      width: { xs: 40, md: 60 }, 
                      height: { xs: 40, md: 60 }, 
                      borderRadius: '50%', 
                      background: `${colors.primary}15`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                      color: colors.primary,
                    }}>
                      {adv.icon}
                    </Box>
                    <Typography sx={{ fontWeight: 700, color: colors.dark, mb: { xs: 0.3, md: 1 }, fontSize: { xs: '0.8rem', md: '1rem' } }}>{adv.title}</Typography>
                    <Typography sx={{ color: colors.gray, fontSize: { xs: '0.7rem', md: '0.9rem' }, display: { xs: 'none', sm: 'block' } }}>{adv.desc}</Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Box sx={{ py: { xs: 5, md: 10 }, background: colors.dark }}>
        <Container maxWidth="lg" sx={{ px: { xs: 1.5, md: 3 } }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 6 } }}>
            <Chip label="מה אומרים עלינו" sx={{ mb: 2, background: 'rgba(255,255,255,0.1)', color: '#fff', fontWeight: 600, fontSize: { xs: '0.75rem', md: '0.875rem' } }} />
            <Typography variant="h2" sx={{ fontWeight: 800, color: '#fff', fontSize: { xs: '1.4rem', sm: '1.6rem', md: '2.5rem' } }}>
              לקוחות מרוצים
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 1.5, md: 3 }}>
            {testimonials.map((t, i) => (
              <Grid item xs={6} md={3} key={i}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <Card sx={{ p: { xs: 1.5, md: 3 }, borderRadius: { xs: 2, md: 3 }, height: '100%', background: colors.darkGray }}>
                    <FormatQuoteIcon sx={{ color: colors.primary, fontSize: { xs: 20, md: 30 }, mb: { xs: 0.5, md: 1 } }} />
                    <Typography sx={{ color: 'rgba(255,255,255,0.9)', mb: { xs: 1, md: 2 }, lineHeight: 1.5, fontSize: { xs: '0.75rem', md: '1rem' } }}>
                      "{t.text}"
                    </Typography>
                    <Rating value={t.rating} readOnly size="small" sx={{ mb: 0.5, '& svg': { fontSize: { xs: 14, md: 18 } } }} />
                    <Typography sx={{ fontWeight: 700, color: '#fff', fontSize: { xs: '0.75rem', md: '1rem' } }}>{t.name}</Typography>
                    <Typography sx={{ color: colors.gray, fontSize: { xs: '0.65rem', md: '0.85rem' } }}>{t.location}</Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ py: { xs: 5, md: 10 }, background: colors.light }}>
        <Container maxWidth="md" sx={{ px: { xs: 1.5, md: 3 } }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 6 } }}>
            <Chip label="שאלות נפוצות" sx={{ mb: 2, background: colors.white, color: colors.primary, fontWeight: 600, fontSize: { xs: '0.75rem', md: '0.875rem' } }} />
            <Typography variant="h2" sx={{ fontWeight: 800, color: colors.dark, fontSize: { xs: '1.4rem', sm: '1.6rem', md: '2.5rem' } }}>
              יש שאלות? יש תשובות
            </Typography>
          </Box>

          {faqItems.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
            >
              <Accordion
                sx={{
                  mb: 2,
                  borderRadius: '12px !important',
                  overflow: 'hidden',
                  '&:before': { display: 'none' },
                  boxShadow: 'none',
                  border: '1px solid #E5E7EB',
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ fontWeight: 600, color: colors.dark }}>{item.q}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography sx={{ color: colors.gray, lineHeight: 1.7 }}>{item.a}</Typography>
                </AccordionDetails>
              </Accordion>
            </motion.div>
          ))}
        </Container>
      </Box>

      {/* Location Section */}
      <Box sx={{ py: { xs: 6, md: 10 }, background: colors.white }}>
        <Container maxWidth="lg" sx={{ px: { xs: 1.5, md: 3 } }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip label="אזור שירות" sx={{ mb: 2, background: colors.light, color: colors.primary, fontWeight: 600 }} />
            <Typography variant="h2" sx={{ fontWeight: 800, color: colors.dark, fontSize: { xs: '1.8rem', md: '2.5rem' }, mb: 2 }}>
              מגיעים לכל אזור המרכז
            </Typography>
            <Typography sx={{ color: colors.gray, maxWidth: 600, mx: 'auto' }}>
              פתח תקווה, תל אביב, רמת גן, גבעתיים, בני ברק, חולון, בת ים, ראשון לציון, הרצליה ועוד
            </Typography>
          </Box>

          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            justifyContent: 'center', 
            gap: 2,
            mb: 4,
          }}>
            {['תל אביב', 'רמת גן', 'פתח תקווה', 'ראשון לציון', 'חולון', 'בת ים', 'גבעתיים', 'הרצליה', 'בני ברק'].map((city, i) => (
              <Chip
                key={i}
                icon={<LocationOnIcon sx={{ color: `${colors.primary} !important` }} />}
                label={city}
                sx={{ 
                  background: colors.light, 
                  fontWeight: 600,
                  py: 2,
                }}
              />
            ))}
          </Box>

          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<PhoneIcon />}
              onClick={handleCall}
              sx={{
                background: colors.primary,
                py: 1.5,
                px: 4,
                borderRadius: 3,
                fontWeight: 700,
                fontSize: '1.1rem',
                gap: 1.5,
                '&:hover': { background: colors.secondary },
              }}
            >
              בדוק אם אנחנו מגיעים אליך
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Final CTA Section */}
      <Box
        sx={{
          py: { xs: 6, md: 10 },
          background: `linear-gradient(135deg, ${colors.dark} 0%, ${colors.primary} 100%)`,
        }}
      >
        <Container maxWidth="md" sx={{ px: { xs: 1.5, md: 3 } }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h2" sx={{ color: '#fff', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, mb: 2 }}>
              נתקעת? אנחנו בדרך!
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.8)', mb: 4, maxWidth: 500, mx: 'auto' }}>
              שיחת טלפון אחת וגרר יוצא אליך. מהיר, מקצועי, ובמחיר הוגן.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button
                variant="contained"
                size="large"
                startIcon={<PhoneIcon />}
                onClick={handleCall}
                sx={{
                  background: '#fff',
                  color: colors.primary,
                  py: 2,
                  px: 4,
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  borderRadius: 3,
                  gap: 1.5,
                  '&:hover': { background: '#f5f5f5' },
                }}
              >
                {PHONE_NUMBER}
              </Button>
              <Button
                variant="contained"
                size="large"
                startIcon={<WhatsAppIcon />}
                onClick={() => handleWhatsApp()}
                sx={{
                  background: '#25D366',
                  py: 2,
                  px: 4,
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  borderRadius: 3,
                  gap: 1.5,
                  '&:hover': { background: '#20BD5A' },
                }}
              >
                וואטסאפ
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ py: 4, background: colors.dark, textAlign: 'center', px: 2 }}>
        <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mb: 2 }}>
          <TwoWheelerIcon sx={{ color: colors.primary }} />
          <Typography sx={{ color: '#fff', fontWeight: 700 }}>הגרריסט</Typography>
        </Stack>
        <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>
          © 2024 הגרריסט - גרר לאופנועים | כל הזכויות שמורות
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.4)', mt: 1, fontSize: '0.85rem' }}>
          שירות 24/7 | פתח תקווה ואזור המרכז | {PHONE_NUMBER}
        </Typography>
      </Box>

      {/* WhatsApp FAB */}
      <motion.div
        style={{ position: 'fixed', bottom: 20, left: 20, zIndex: 1000 }}
        animate={{ scale: [1, 1.1, 1] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <Button
          variant="contained"
          onClick={() => handleWhatsApp()}
          sx={{
            width: { xs: 56, md: 64 },
            height: { xs: 56, md: 64 },
            borderRadius: '50%',
            minWidth: 0,
            background: '#25D366',
            boxShadow: '0 8px 25px rgba(37, 211, 102, 0.4)',
            '&:hover': { background: '#20BD5A' },
          }}
        >
          <WhatsAppIcon sx={{ fontSize: { xs: 28, md: 32 } }} />
        </Button>
      </motion.div>

      {/* Call FAB */}
      <motion.div
        style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 1000 }}
        animate={{ scale: [1, 1.1, 1] }}
        transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
      >
        <Button
          variant="contained"
          onClick={handleCall}
          sx={{
            width: { xs: 56, md: 64 },
            height: { xs: 56, md: 64 },
            borderRadius: '50%',
            minWidth: 0,
            background: colors.primary,
            boxShadow: '0 8px 25px rgba(220, 38, 38, 0.4)',
            '&:hover': { background: colors.secondary },
          }}
        >
          <PhoneIcon sx={{ fontSize: { xs: 28, md: 32 } }} />
        </Button>
      </motion.div>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={4000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          הפרטים נשלחו! נחזור אליך בהקדם 🏍️
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HagraristLandingPage;
